import * as React from 'react'
import {useMemo} from 'react'
import {FormattedDate, FormattedTime, useIntl} from 'react-intl'
import isValid from 'date-fns/isValid'

import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'

export type FormatToken =
  | 'date-short'
  | 'date-long'
  | 'datetime-short'
  | 'datetime-long'
  | 'date-longform'
  | 'date-longform-weekday'
  | 'datetime-longform'
  | 'datetime-longform-weekday'
  | 'date-short-year'
  | 'date-long-year'
  | 'datetime-short-year'
  | 'datetime-long-year'
  | 'date-longform-year'
  | 'date-longform-weekday-year'
  | 'datetime-longform-year'
  | 'datetime-longform-weekday-year'
  | 'timefirst-longform'
  | 'timefirst-longform-weekday'
  | 'timefirst-longform-year'
  | 'timefirst-longform-weekday-year'
  | 'time'

export interface UnifiedDateTimeProps {
  /**
   * The date or timestamp to format
   */
  value: Date | string | number
  /**
   * The format token that determines how the date/time should be displayed
   */
  format: FormatToken
  /**
   * Optional timezone to display the time in (defaults to user's timezone)
   */
  timeZone?: string
}

/**
 * Helper component that formats time with timezone in parentheses
 */
interface FormattedTimeWithParenthesesProps {
  value: Date
  timeZone?: string
}

const FormattedTimeWithParentheses: React.FC<
  FormattedTimeWithParenthesesProps
> = ({value, timeZone}) => {
  const intl = useIntl()
  const WHITESPACE = ' '

  // Get just the timezone abbreviation using Intl.DateTimeFormat
  const getTimezoneAbbreviation = (date: Date, tz?: string) => {
    const formatter = new Intl.DateTimeFormat(intl.locale, {
      timeZone: tz,
      timeZoneName: 'short'
    })

    const parts = formatter.formatToParts(date)
    const timeZonePart = parts.find(part => part.type === 'timeZoneName')
    return timeZonePart?.value || ''
  }

  const timezoneAbbreviation = getTimezoneAbbreviation(value, timeZone)

  return (
    <>
      <FormattedTime
        value={value}
        hour="2-digit"
        minute="2-digit"
        hour12={false}
        timeZone={timeZone}
      />
      {timezoneAbbreviation && `${WHITESPACE}(${timezoneAbbreviation})`}
    </>
  )
}

/**
 * Get the current year to determine if we should show the year in the date
 */
const getCurrentYear = (): number => new Date().getFullYear()

/**
 * Get the year from a date value
 */
const getDateYear = (date: Date): number => date.getFullYear()

/**
 * Check if the year should be included based on the format token and date
 */
const shouldIncludeYear = (date: Date, format: FormatToken): boolean => {
  // Always include year for tokens that explicitly include "-year"
  if (format.includes('-year')) {
    return true
  }

  const currentYear = getCurrentYear()
  const dateYear = getDateYear(date)

  // Include year if the date is in the next calendar year
  return dateYear === currentYear + 1
}

/**
 * Normalize the input value to a Date object
 */
const normalizeDate = (value: Date | string | number): Date => {
  if (value instanceof Date) {
    return value
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return new Date(value)
  }

  return new Date()
}

/**
 * UnifiedDateTime component that provides consistent date and time formatting
 * across the application using react-intl components
 */
export const UnifiedDateTime: React.FC<UnifiedDateTimeProps> = ({
  value,
  format,
  timeZone
}) => {
  const date = useMemo(() => normalizeDate(value), [value])

  const includeYear = useMemo(
    () => shouldIncludeYear(date, format),
    [date, format]
  )

  // Return empty string for invalid dates
  if (!isValid(date)) {
    return null
  }

  // Date-only formats
  switch (format) {
    case 'date-short':
    case 'date-short-year':
      return (
        <FormattedDate
          value={date}
          month="short"
          day="numeric"
          year={includeYear ? 'numeric' : undefined}
        />
      )

    case 'date-long':
    case 'date-long-year':
      return (
        <FormattedDate
          value={date}
          weekday="short"
          month="short"
          day="numeric"
          year={includeYear ? 'numeric' : undefined}
        />
      )

    case 'date-longform':
    case 'date-longform-year':
      return (
        <FormattedDate
          value={date}
          month="long"
          day="numeric"
          year={includeYear ? 'numeric' : undefined}
        />
      )

    case 'date-longform-weekday':
    case 'date-longform-weekday-year':
      return (
        <FormattedDate
          value={date}
          weekday="long"
          month="long"
          day="numeric"
          year={includeYear ? 'numeric' : undefined}
        />
      )

    // DateTime formats
    case 'datetime-short':
    case 'datetime-short-year':
      return (
        <FormattedMessageWrapper
          id="dateThenTime"
          defaultMessage="{date} at {time}"
          values={{
            date: (
              <FormattedDate
                value={date}
                month="short"
                day="numeric"
                year={includeYear ? 'numeric' : undefined}
              />
            ),
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            )
          }}
        />
      )

    case 'datetime-long':
    case 'datetime-long-year':
      return (
        <FormattedMessageWrapper
          id="dateThenTime"
          defaultMessage="{date} at {time}"
          values={{
            date: (
              <FormattedDate
                value={date}
                weekday="short"
                month="short"
                day="numeric"
                year={includeYear ? 'numeric' : undefined}
              />
            ),
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            )
          }}
        />
      )

    case 'datetime-longform':
    case 'datetime-longform-year':
      return (
        <FormattedMessageWrapper
          id="dateThenTime"
          defaultMessage="{date} at {time}"
          values={{
            date: (
              <FormattedDate
                value={date}
                month="long"
                day="numeric"
                year={includeYear ? 'numeric' : undefined}
              />
            ),
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            )
          }}
        />
      )

    case 'datetime-longform-weekday':
    case 'datetime-longform-weekday-year':
      return (
        <FormattedMessageWrapper
          id="dateThenTime"
          defaultMessage="{date} at {time}"
          values={{
            date: (
              <FormattedDate
                value={date}
                weekday="long"
                month="long"
                day="numeric"
                year={includeYear ? 'numeric' : undefined}
              />
            ),
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            )
          }}
        />
      )

    // Time-first formats
    case 'timefirst-longform':
      return (
        <FormattedMessageWrapper
          id="timeThenDate"
          defaultMessage="{time} on {date}"
          values={{
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            ),
            date: (
              <FormattedDate
                value={date}
                month="long"
                day="numeric"
                year={includeYear ? 'numeric' : undefined}
              />
            )
          }}
        />
      )

    case 'timefirst-longform-weekday':
      return (
        <FormattedMessageWrapper
          id="timeThenDateWeek"
          defaultMessage="{time} on {weekdayDate}"
          values={{
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            ),
            weekdayDate: (
              <FormattedDate
                value={date}
                weekday="long"
                month="long"
                day="numeric"
                year={includeYear ? 'numeric' : undefined}
              />
            )
          }}
        />
      )

    case 'timefirst-longform-year':
      return (
        <FormattedMessageWrapper
          id="timeThenDate"
          defaultMessage="{time} on {date}"
          values={{
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            ),
            date: (
              <FormattedDate
                value={date}
                month="long"
                day="numeric"
                year="numeric"
              />
            )
          }}
        />
      )

    case 'timefirst-longform-weekday-year':
      return (
        <FormattedMessageWrapper
          id="timeThenDateWeek"
          defaultMessage="{time} on {weekdayDate}"
          values={{
            time: (
              <FormattedTimeWithParentheses value={date} timeZone={timeZone} />
            ),
            weekdayDate: (
              <FormattedDate
                value={date}
                weekday="long"
                month="long"
                day="numeric"
                year="numeric"
              />
            )
          }}
        />
      )

    case 'time':
      return <FormattedTimeWithParentheses value={date} timeZone={timeZone} />

    default:
      // Fallback to a basic date format if format is not recognized
      return (
        <FormattedDate
          value={date}
          month="short"
          day="numeric"
          year={includeYear ? 'numeric' : undefined}
        />
      )
  }
}

export default UnifiedDateTime
